import React, { useState } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { Pagination } from '../../../components/Pagination';
import moment from 'moment';
import { cameraService } from '../../../services/central-api/cameras';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import Tooltip from '../../../components/Tooltip';
import MyDataTable from '../../../components/MyDataTable';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function CamerasList({ navigation }: { navigation: Navigation; }) {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const route = useRoute<RouteProp<CamerasParamList, 'CamerasList'>>();
    const [filters, setFilters] = useFilters<CamerasParamList['CamerasList']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasPaginatedList', filters],
        queryFn: () => cameraService.getCamerasPaginated({
            ...filters,
            showDisabled: false,
            hideChildren: false,
        }),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header title={translate('cameras')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredCameras')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('title'),
                                selector: row => row.title,
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('serialNo'),
                                selector: row => row.serialNo || '',
                            },
                            {
                                name: translate('type'),
                                selector: row => translate(row.type),
                            },
                            {
                                name: translate('integration'),
                                selector: row => row.thirdPartyCamera?.thirdPartyCloudProvider && row.thirdPartyCamera?.thirdPartyImageProvider ? `${row.thirdPartyCamera.thirdPartyCloudProvider.tradeName} - ${row.thirdPartyCamera.thirdPartyImageProvider.tradeName}` : row.thirdPartyCamera?.origin ?? '',
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                cell: row => {
                                    return row.lastTimeOnline ?
                                        <Tooltip tooltipDirection='right' tooltipContent={translate('offlineSince', {
                                            lastTimeOnline: moment(row.lastTimeOnline).format('DD/MM/YYYY HH:mm')
                                        })} style={{ width: 300 }}>
                                            <MyAppText style={[styles.cell, row.isConnected ? {} : { color: '#FF0027' }]}>
                                                {row.isConnected ? translate('online') : translate('offline')}
                                            </MyAppText>
                                        </Tooltip>
                                        : <MyAppText style={[styles.cell, row.isConnected ? {} : { color: '#FF0027' }]}>
                                            {row.isConnected ? translate('online') : translate('offline')}
                                        </MyAppText>;
                                }
                            },
                            {
                                name: translate('tags'),
                                grow: 3,
                                cell: row => {
                                    if (!row.tags) {
                                        return '';
                                    }

                                    const tags = row.tags.map((tag) =>
                                        <View key={tag.id} style={[styles.marker, { backgroundColor: tag.color }]}>
                                            <MyAppText style={styles.markerText(tag.color)}>
                                                {tag.name}
                                            </MyAppText>
                                        </View>
                                    );

                                    return <View style={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        width: '100%',
                                        gap: 5
                                    }}>
                                        {tags}
                                    </View>;
                                },
                            },
                            {
                                name: translate('registeredAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                name: '',
                                button: true,
                                wrap: true,
                                cell: row => {

                                    const actions: Action[] = [{
                                        content: translate('watchCamera'),
                                        onPress: () => {
                                            navigation.navigate('CameraPlayer', { id: row.id });
                                        },
                                    }, {
                                        content: translate('watchCameraOnNewTab'),
                                        onPress: () => {
                                            window.open(`/cameras/map/${row.id}`);
                                        },
                                    }, {
                                        content: authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_management ? translate('editInformation') : translate('seeInformation'),
                                        onPress: () => {
                                            navigation.navigate('CameraDetails', { id: row.id });
                                        },
                                    }];

                                    if ((authenticatedUser?.isAdmin || authenticatedUser?.permissions.crm_module_access) && !row.thirdPartyCamera) {
                                        actions.push({
                                            content: translate('openOnCrm'),
                                            onPress: () => {
                                                navigation.navigate('ManagementDetails', { id: row.id });
                                            },
                                        });
                                    }

                                    return (
                                        <DropdownButton
                                            actions={actions}
                                        />
                                    );

                                }
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    marker: {
        borderRadius: 8,
        minHeight: 16,
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: '100%'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11,
    }),
    cell: {
        fontSize: 14,
        color: theme.colors.textColor,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
}));

